import { useState } from 'react';
import { Eye, EyeOff, Plus, Lightbulb } from 'lucide-react';
import { Input } from './ui';

export function PasswordInput({
  value, onChange, placeholder, autoFocus, size = 'lg',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  size?: 'md' | 'lg';
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="pw-wrap">
      <Input
        type={show ? 'text' : 'password'}
        size={size}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
      />
      <button
        type="button"
        className="toggle"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? 'Passwort verbergen' : 'Passwort anzeigen'}
        tabIndex={-1}
      >
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
}

const COMMON_PW = new Set(['passwort','password','123456','12345678','qwertz','qwerty','admin','geheim','111111','gaspoltshofen']);

export function scorePassword(pw: string) {
  if (!pw) return { score: 0, label: '', color: 'var(--text-quaternary)', hints: ['Mindestens 8 Zeichen'] };
  if (COMMON_PW.has(pw.toLowerCase())) {
    return { score: 0, label: 'Zu einfach', color: 'var(--danger)', hints: ['Dieses Passwort ist zu bekannt – wähle etwas anderes'] };
  }
  let s = 0;
  const hints: string[] = [];
  if (pw.length >= 8) s++; else hints.push('Mindestens 8 Zeichen');
  if (pw.length >= 12) s++; else if (pw.length >= 8) hints.push('Mache es länger (12+ Zeichen)');
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++; else hints.push('Mische Groß- und Kleinbuchstaben');
  if (/\d/.test(pw)) s++; else hints.push('Füge eine Zahl hinzu');
  if (/[^A-Za-z0-9]/.test(pw)) s = Math.min(4, s + 1);
  s = Math.min(4, s);
  const meta = [
    { label: 'Schwach', color: 'var(--danger)' },
    { label: 'Schwach', color: 'var(--danger)' },
    { label: 'OK', color: 'var(--warning)' },
    { label: 'Stark', color: 'var(--success)' },
    { label: 'Sehr stark', color: '#15803d' },
  ][s]!;
  return { score: s, label: meta.label, color: meta.color, hints: hints.slice(0, 2) };
}

export function PasswordStrength({ pw }: { pw: string }) {
  const r = scorePassword(pw);
  return (
    <div className="pw-strength">
      <div className="pw-strength-bars">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="seg" style={{ background: i < r.score ? r.color : undefined }} />
        ))}
      </div>
      {pw && (
        <div className="pw-strength-label">
          <span style={{ color: r.color }}>{r.label}</span>
        </div>
      )}
      {r.hints.map((h, i) => (
        <div className="pw-hint" key={i}>
          <Plus size={11} /> {h}
        </div>
      ))}
    </div>
  );
}

export function PasswordTip() {
  return (
    <div className="muted-card" style={{ fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'center' }}>
      <Lightbulb size={14} className="text-accent" /> Tipp: Drei zufällige Wörter sind oft sicherer und einfacher zu merken.
    </div>
  );
}
